import { Button, Dialog, Flex, Text, createForm } from '@applyboard/crystal-ui';
import { useMemo, useState } from 'react';

type ProgramFormFields = {
  code: string;
  name: string;
  description: string;
  programLevelName: string;
  programType: string;
  fieldOfStudyId: string;
  programLength: string;
  lengthUnit: string;
  commitment: string;
  isLanguageProgram: string;
  url: string;
  deliveryMethod: string;
  workPracticum: string;
  applicationFee: string;
  tuition: string;
  tuitionPeriod: string;
  isPublished: string;
};

const FORM_FIELD_NAMES: (keyof ProgramFormFields)[] = [
  'code',
  'name',
  'description',
  'programLevelName',
  'programType',
  'fieldOfStudyId',
  'programLength',
  'lengthUnit',
  'commitment',
  'isLanguageProgram',
  'url',
  'deliveryMethod',
  'workPracticum',
  'applicationFee',
  'tuition',
  'tuitionPeriod',
  'isPublished',
];

export function normalizeFormValues(values: Partial<ProgramFormFields>): ProgramFormFields {
  return {
    code: values.code || '',
    name: values.name || '',
    description: values.description || '',
    programLevelName: values.programLevelName || '',
    programType: values.programType || '',
    fieldOfStudyId: values.fieldOfStudyId || '',
    programLength: values.programLength || '',
    lengthUnit: values.lengthUnit || '',
    commitment: values.commitment || '',
    isLanguageProgram: values.isLanguageProgram || '',
    url: values.url || '',
    deliveryMethod: values.deliveryMethod || '',
    workPracticum: values.workPracticum || '',
    applicationFee: values.applicationFee || '',
    tuition: values.tuition || '',
    tuitionPeriod: values.tuitionPeriod || '',
    isPublished: values.isPublished || '',
  };
}

export function hasFormChanges(values: Partial<ProgramFormFields>, defaultValues: ProgramFormFields): boolean {
  const normalizedValues = normalizeFormValues(values);
  return JSON.stringify(normalizedValues) !== JSON.stringify(defaultValues);
}

type UseFormChangesParams = {
  useFieldValues: ReturnType<typeof createForm<ProgramFormFields>>['useFieldValues'];
  formDefaultValues: ProgramFormFields;
};

function useFormChanges({ useFieldValues, formDefaultValues }: UseFormChangesParams) {
  const fieldValues = useFieldValues(FORM_FIELD_NAMES);

  const normalizedCurrentValues = useMemo(() => {
    return normalizeFormValues(fieldValues);
  }, [fieldValues]);

  const hasChanges = useMemo(() => {
    return hasFormChanges(normalizedCurrentValues, formDefaultValues);
  }, [normalizedCurrentValues, formDefaultValues]);

  return { hasChanges, fieldValues: normalizedCurrentValues };
}

type ProgramFormActionsProps = {
  allowedToEdit: boolean;
  isPatchingProgram: boolean;
  useFieldValues: ReturnType<typeof createForm<ProgramFormFields>>['useFieldValues'];
  useSetFieldValues: ReturnType<typeof createForm<ProgramFormFields>>['useSetFieldValues'];
  formDefaultValues: ProgramFormFields;
  setAllowedToEdit: (allowedToEdit: boolean) => void;
};

export function ProgramFormActions({
  allowedToEdit,
  isPatchingProgram,
  useFieldValues,
  useSetFieldValues,
  formDefaultValues,
  setAllowedToEdit,
}: ProgramFormActionsProps) {
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const { hasChanges } = useFormChanges({ useFieldValues, formDefaultValues });

  return (
    <>
      {allowedToEdit ? (
        <Flex basis="100%" justify="end" pt={4}>
          <Flex gap={2}>
            <CancelButton
              useFieldValues={useFieldValues}
              formDefaultValues={formDefaultValues}
              onCancel={() => setAllowedToEdit(false)}
              onShowModal={() => setShowDiscardModal(true)}
            />
            <Button intent="primary" emphasis="contained" type="submit" disabled={isPatchingProgram || !hasChanges}>
              {isPatchingProgram ? 'Saving...' : 'Save Program'}
            </Button>
          </Flex>
        </Flex>
      ) : null}
      <FormResetHandler
        useSetFieldValues={useSetFieldValues}
        formDefaultValues={formDefaultValues}
        showDiscardModal={showDiscardModal}
        setShowDiscardModal={setShowDiscardModal}
        setAllowedToEdit={setAllowedToEdit}
      />
    </>
  );
}

type CancelButtonProps = {
  useFieldValues: ReturnType<typeof createForm<ProgramFormFields>>['useFieldValues'];
  formDefaultValues: ProgramFormFields;
  onCancel: () => void;
  onShowModal: () => void;
};

function CancelButton({ useFieldValues, formDefaultValues, onCancel, onShowModal }: CancelButtonProps) {
  const { hasChanges } = useFormChanges({ useFieldValues, formDefaultValues });

  const handleCancel = () => {
    if (hasChanges) {
      onShowModal();
    } else {
      onCancel();
    }
  };

  return (
    <Button intent="secondary" emphasis="transparent" onClick={handleCancel}>
      Cancel
    </Button>
  );
}

type FormResetHandlerProps = {
  useSetFieldValues: ReturnType<typeof createForm<ProgramFormFields>>['useSetFieldValues'];
  formDefaultValues: ProgramFormFields;
  showDiscardModal: boolean;
  setShowDiscardModal: (open: boolean) => void;
  setAllowedToEdit: (allowedToEdit: boolean) => void;
};

function FormResetHandler({
  useSetFieldValues,
  formDefaultValues,
  showDiscardModal,
  setShowDiscardModal,
  setAllowedToEdit,
}: FormResetHandlerProps) {
  const setFieldValues = useSetFieldValues();

  const handleConfirm = () => {
    setFieldValues(formDefaultValues);
    setShowDiscardModal(false);
    setAllowedToEdit(false);
  };

  return <DiscardChangesModal open={showDiscardModal} onOpenChange={setShowDiscardModal} onConfirm={handleConfirm} />;
}

type DiscardChangesModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
};

function DiscardChangesModal({ open, onOpenChange, onConfirm }: DiscardChangesModalProps) {
  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose} size="md">
      <Dialog.Heading>Discard Changes?</Dialog.Heading>
      <Dialog.Content>
        <Text variant="bodyM">
          You have unsaved changes. <br /> Are you sure you want to discard them?
        </Text>
      </Dialog.Content>
      <Dialog.Actions>
        <Button width="fill" intent="secondary" emphasis="highlighted" onClick={handleClose}>
          Back to editing
        </Button>
        <Button width="fill" intent="negative" onClick={onConfirm}>
          Discard
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
